import { type FormFieldResponse, FormFieldType, type FormResponse } from '@components/DriverManagement/DriverForms/api/schema'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Divider, Drawer } from 'antd'
import React from 'react'

export type ResponseDrawerPayload = FormResponse & {
  visible: boolean
}

const isEmptyObject = (obj: any) => {
  return typeof obj === 'object' && Object.keys(obj).length === 0
}

function FieldValueRenderer({ field_type, value }) {
  if (!value || isEmptyObject(value)) {
    return <Texto appearance='medium'>Empty</Texto>
  }
  if (field_type === FormFieldType.DATE) {
    return <Texto>{formatters.DATE(value)}</Texto>
  }
  if (field_type === FormFieldType.DATERANGE) {
    const { startDate, endDate } = value
    return (
      <Texto>
        {formatters.DATE(startDate)} - {formatters.DATE(endDate)}
      </Texto>
    )
  }
  return <Texto>{value}</Texto>
}

function ResponseField({ response }: { response: FormFieldResponse }) {
  return (
    <>
      <Horizontal>
        <Texto weight='bold'>{response.field_name}</Texto>
        <Texto className='ml-1'>({response.required ? 'Required' : 'Optional'})</Texto>
      </Horizontal>
      <Horizontal>
        <FieldValueRenderer value={response.value} field_type={response.field_type} />
      </Horizontal>
      <Divider />
    </>
  )
}

export function ResponseDrawer({
  drawerPayload,
  setDrawerPayload,
}: {
  drawerPayload: ResponseDrawerPayload
  setDrawerPayload: (x: ResponseDrawerPayload) => void
}) {
  if (!drawerPayload) {
    return null
  }

  const titleComponent = (
    <Vertical>
      <Horizontal justifyContent='space-between' className='mr-2 mb-0'>
        <Texto>{drawerPayload.name}</Texto>
      </Horizontal>
      <Horizontal justifyContent='space-between' className='mr-2 mb-0'>
        <Texto category='label' appearance='medium'>
          {drawerPayload.driver_name}
        </Texto>
        <Texto category='label' appearance='medium'>
          {formatters.DATE_TIME(drawerPayload.date)}
        </Texto>
      </Horizontal>
    </Vertical>
  )

  return (
    <Drawer
      title={titleComponent}
      visible={drawerPayload.visible}
      placement='right'
      width={450}
      bodyStyle={{ paddingTop: '0' }}
      onClose={() => setDrawerPayload({ ...drawerPayload, visible: false })}
    >
      <Divider className='mt-0' style={{ backgroundColor: 'var(--gray-300)' }} />
      <Vertical style={{ overflowY: 'scroll' }}>
        {drawerPayload.response?.map((response, idx) => (
          <ResponseField response={response} key={idx} />
        ))}
      </Vertical>
    </Drawer>
  )
}
